import { useCallback, useEffect, useState } from "react";
import accountsService from "../service/accountsService";

const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [journals, setJournals] = useState([]);
  const [journalLines, setJournalLines] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const wrap = async (fn) => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err.message || "Something went wrong");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = useCallback(
    (filters = {}) =>
      wrap(async () => {
        const data = await accountsService.getAccounts(filters);
        setAccounts(data);
        return data;
      }),
    [],
  );

  const loadInvoices = useCallback(
    (filters = {}) =>
      wrap(async () => {
        const data = await accountsService.getInvoices(filters);
        setInvoices(data);
        return data;
      }),
    [],
  );

  const loadBills = useCallback(
    (filters = {}) =>
      wrap(async () => {
        const data = await accountsService.getBills(filters);
        setBills(data);
        return data;
      }),
    [],
  );

  const loadJournals = useCallback(
    (filters = {}) =>
      wrap(async () => {
        const data = await accountsService.getJournals(filters);
        setJournals(data);
        return data;
      }),
    [],
  );

  const loadJournalLines = useCallback(
    (journalId) =>
      wrap(async () => {
        const data = await accountsService.getJournalLines(journalId);
        setJournalLines(data);
        return data;
      }),
    [],
  );

  const loadBankAccounts = useCallback(
    () =>
      wrap(async () => {
        const data = await accountsService.getBankAccounts();
        setBankAccounts(data);
        return data;
      }),
    [],
  );

  const createAccount = useCallback(
    (payload) =>
      wrap(async () => {
        const data = await accountsService.createAccount(payload);
        await loadAccounts();
        return data;
      }),
    [loadAccounts],
  );

  const updateAccount = useCallback(
    (id, payload) =>
      wrap(async () => {
        const data = await accountsService.updateAccount(id, payload);
        await loadAccounts();
        return data;
      }),
    [loadAccounts],
  );

  const deleteAccount = useCallback(
    (id) =>
      wrap(async () => {
        const data = await accountsService.deleteAccount(id);
        await loadAccounts();
        return data;
      }),
    [loadAccounts],
  );

  const createInvoice = useCallback(
    (payload) =>
      wrap(async () => {
        const data = await accountsService.createInvoice(payload);
        await loadInvoices();
        return data;
      }),
    [loadInvoices],
  );

  const updateInvoice = useCallback(
    (id, payload) =>
      wrap(async () => {
        const data = await accountsService.updateInvoice(id, payload);
        await loadInvoices();
        return data;
      }),
    [loadInvoices],
  );

  const createBill = useCallback(
    (payload) =>
      wrap(async () => {
        const data = await accountsService.createBill(payload);
        await loadBills();
        return data;
      }),
    [loadBills],
  );

  const updateBill = useCallback(
    (id, payload) =>
      wrap(async () => {
        const data = await accountsService.updateBill(id, payload);
        await loadBills();
        return data;
      }),
    [loadBills],
  );

  const createJournal = useCallback(
    (payload) =>
      wrap(async () => {
        const data = await accountsService.createJournal(payload);
        await loadJournals();
        return data;
      }),
    [loadJournals],
  );

  const updateJournal = useCallback(
    (id, payload) =>
      wrap(async () => {
        const data = await accountsService.updateJournal(id, payload);
        await loadJournals();
        return data;
      }),
    [loadJournals],
  );

  const createReceipt = useCallback(
    (payload) =>
      wrap(async () => {
        return await accountsService.createReceipt(payload);
      }),
    [],
  );

  const createPayment = useCallback(
    (payload) =>
      wrap(async () => {
        return await accountsService.createPayment(payload);
      }),
    [],
  );

  const createBankAccount = useCallback(
    (payload) =>
      wrap(async () => {
        const data = await accountsService.createBankAccount(payload);
        await loadBankAccounts();
        return data;
      }),
    [loadBankAccounts],
  );

  useEffect(() => {
    loadAccounts();
    loadInvoices();
    loadBills();
    loadJournals();
    loadBankAccounts();
  }, [loadAccounts, loadInvoices, loadBills, loadJournals, loadBankAccounts]);

  return {
    accounts,
    invoices,
    bills,
    journals,
    journalLines,
    bankAccounts,
    loading,
    error,
    setError,
    loadAccounts,
    loadInvoices,
    loadBills,
    loadJournals,
    loadJournalLines,
    loadBankAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    createInvoice,
    updateInvoice,
    createBill,
    updateBill,
    createJournal,
    updateJournal,
    createReceipt,
    createPayment,
    createBankAccount,
  };
};

export default useAccounts;
